import React from 'react'
import {Link as RouterLink} from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import {Box} from '../../Box'
import {TableCell} from '../TableCell'
import {Link} from '../../Link'
import type {CellTooltipPlacement} from './TableHeadCell'

interface Props {
  dense: boolean
  first: boolean
  maxWidth?: number
  minWidth?: number
  linkTo?: string // when provided, make the whole cell a real anchor
  tooltip?: string
  tooltipPlacement?: CellTooltipPlacement
  isExtra?: boolean
  isExtraGroupStart?: boolean
}

// helper to extract a plain-text label from React children (best-effort)
function getTextFromChildren(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getTextFromChildren).join('')
  if (React.isValidElement(node)) {
    return getTextFromChildren(node.props?.children)
  }
  return ''
}

const TOOLTIP_ENTER_DELAY = 100

export const SortablePaginatedTableCell: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  dense,
  first,
  maxWidth,
  minWidth,
  linkTo,
  tooltip,
  tooltipPlacement,
  isExtra,
  isExtraGroupStart,
}) => {
  // state & refs for truncation detection (tooltip only when really truncated)
  const ellipsisRef = React.useRef<HTMLDivElement | null>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  const fullText = React.useMemo(() => getTextFromChildren(children), [children])

  const computeTruncation = React.useCallback(() => {
    const el = ellipsisRef.current
    if (!el) return
    // Compare scrollWidth vs clientWidth to know if ellipsis is active
    const truncated = el.scrollWidth > el.clientWidth + 1 // +1 guards tiny rounding diffs
    setIsTruncated(truncated)
  }, [])

  React.useEffect(() => {
    computeTruncation()
  }, [computeTruncation, children, maxWidth, minWidth])

  // update on container size changes as well
  React.useEffect(() => {
    const el = ellipsisRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => computeTruncation())
    ro.observe(el)
    return () => ro.disconnect()
  }, [computeTruncation])

  // Resolve the effective tooltip: explicit tooltip wins, then truncation fallback
  const effectiveTooltip = tooltip ?? (isTruncated ? fullText : '')

  const cellSx =
    isExtra || isExtraGroupStart
      ? {
          ...(isExtra
            ? {
                backgroundColor: 'action.hover',
                color: 'text.secondary',
              }
            : null),
          ...(isExtraGroupStart
            ? {
                borderLeft: '1px solid',
                borderColor: 'divider',
              }
            : null),
        }
      : undefined

  // the element that actually gets ellipsis styles (use `style`, not `sx`)
  const Ellipsis = (
    <div
      ref={ellipsisRef}
      // Inline styles so they apply to the custom Box/content consistently
      style={{
        display: 'inline-block',
        minWidth: minWidth ? `${minWidth}px` : 0,
        maxWidth: maxWidth ? `${maxWidth}px` : '100%',
        width: '100%',
        whiteSpace: 'nowrap',
        overflow: maxWidth ? 'hidden' : 'visible',
        textOverflow: maxWidth ? 'ellipsis' : 'clip',
        verticalAlign: 'middle',
      }}
    >
      {children}
    </div>
  )

  // Wrapper for alignment
  const Inner = (
    <Box
      display={first ? 'block' : 'flex'}
      alignItems={first ? undefined : 'center'}
      // Keep no wrapping on the container; ellipsis happens inside <div> above
      style={{
        whiteSpace: 'nowrap',
        overflow: 'visible',
        textOverflow: 'clip',
        maxWidth: '100%',
      }}
    >
      {Ellipsis}
    </Box>
  )

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'block',
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
    pointerEvents: 'auto',
  }

  const OverlayLink = linkTo ? (
    effectiveTooltip ? (
      <Tooltip
        title={effectiveTooltip}
        enterDelay={TOOLTIP_ENTER_DELAY}
        placement={tooltipPlacement?.placement ?? 'top'}
        slotProps={tooltipPlacement?.offset ? {popper: {modifiers: [{name: 'offset', options: {offset: tooltipPlacement.offset}}]}} : undefined}
      >
        <span style={overlayStyle}>
          <Link
            component={RouterLink}
            to={linkTo}
            aria-label={effectiveTooltip}
            style={{display: 'block', width: '100%', height: '100%', textDecoration: 'none', color: 'inherit'}}
          />
        </span>
      </Tooltip>
    ) : (
      <Link
        component={RouterLink}
        to={linkTo}
        aria-label={isTruncated ? fullText : undefined}
        title={isTruncated ? fullText : undefined}
        style={overlayStyle}
      />
    )
  ) : null

  const wrappedContent = (
    <>
      {Inner}
      {OverlayLink}
    </>
  )

  if (first) {
    return (
      <TableCell
        component="th"
        scope="row"
        padding={dense ? 'checkbox' : 'normal'}
        sx={cellSx}
        // overlay anchor needs a positioned ancestor
        style={{position: linkTo ? ('relative' as const) : undefined}}
      >
        {wrappedContent}
      </TableCell>
    )
  }

  return (
    <TableCell padding={dense ? 'checkbox' : 'normal'} sx={cellSx} style={{position: linkTo ? ('relative' as const) : undefined}}>
      {wrappedContent}
    </TableCell>
  )
}
